import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  createSellEmployeeProducts,
} from "./thunks";
import { EmployeeSale, Product } from "@/types";
import { toast } from "react-toastify";

interface PaginatedProducts {
  data: Product[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface PaginatedSellEmployeeProducts {
  data: EmployeeSale[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface ProductsState {
  products: PaginatedProducts;
  sellEmployee: PaginatedSellEmployeeProducts;
  loading: boolean;
  error: string | null;
  fetched: boolean;
}

const initialState: ProductsState = {
  products: { data: [] },
  sellEmployee: { data: [] },
  loading: false,
  error: null,
  fetched: false,
};

const ProductSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    clearSuppliers: (state) => {
      state.products = { data: [] };
      state.loading = false;
      state.error = null;
      state.fetched = false;
    },
    resetFetched: (state) => {
      state.fetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.fetched = true;
        const payload = action.payload;

        if (Array.isArray(payload.data)) {
          // Dados não paginados
          state.products = { data: payload.data };
        } else {
          // Dados paginados
          state.products = {
            data: payload.data.data,
            total: payload.data.total,
            currentPage: payload.data.current_page,
            perPage: payload.data.per_page,
          };
        }
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        state.fetched = false;
      })
      // Create
      .addCase(createProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProduct.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        //state.products.data.push(action.payload.data);
        toast.success("Produto cadastrado com sucesso!");
      })
      .addCase(createProduct.rejected, (state, action) => {
        const payload = action.payload as { message: string };

        state.error = payload.message;
        state.loading = false;
        toast.error(
          payload.message ||
            "Não foi possível cadastrar o produto, tente novamente!",
        );
      })

      // Update
      .addCase(updateProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProduct.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        const index = state.products.data.findIndex(
          (s) => s.id == action.payload.data.id,
        );
        if (index !== -1) {
          state.products.data[index] = action.payload.data;
        }
        toast.success("Produto atualizado com sucesso!");
      })
      .addCase(updateProduct.rejected, (state, action) => {
        const payload = action.payload as { message: string };
        state.error = payload.message;
        state.loading = false;
        toast.error(
          payload.message ||
            "Não foi possível atualizar o produto, tente novamente!",
        );
      })

      // Delete
      .addCase(deleteProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProduct.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.products.data = state.products.data.filter(
          (s) => String(s.id) != action.payload.data.id,
        );
        toast.success("Produto excluído com sucesso!");
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        const payload = action.payload as { message: string };
        state.error = payload.message;
        state.loading = false;
        toast.error(
          payload.message ||
            "Não foi possível excluir o produto, tente novamente!",
        );
      })

      // create sale product employee
      .addCase(createSellEmployeeProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createSellEmployeeProducts.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          //state.products.data.push(action.payload.data);
          toast.success(
            "Venda de produto para funcionário cadastrada com sucesso!",
          );
        },
      )
      .addCase(createSellEmployeeProducts.rejected, (state, action) => {
        const payload = action.payload as { message: string };
        state.error = payload.message;
        state.loading = false;
        toast.error(
          payload.message ||
            "Não foi possível cadastrar a venda de produto para funcionário, tente novamente!",
        );
      });
  },
});

export const { clearSuppliers, resetFetched } = ProductSlice.actions;
export default ProductSlice.reducer;
